import React, { useState, useEffect, useCallback } from 'react';
import { usePermissions } from '../../permissions/usePermissions';
import { Table, Input, Button, message, Card, Space, Typography } from 'antd';
import axios from 'axios';

const { Title } = Typography;

const CounterManagement = () => {
  const [counters, setCounters] = useState([]);
  const [counterValues, setCounterValues] = useState({});
  const [loading, setLoading] = useState(false);
  const API_URL = `${process.env.REACT_APP_API_BASE_URL}`;
  
  const { can } = usePermissions();
  const canReadCounters = can('counters');
  const canReadReturnCounters = can('returnCounters');
  const canUpdateCounter = name => can('counters', 'update') || (name === 'avoir1' && can('returnCounters', 'update'));
  
  const fetchCounters = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/counter/getall`);
      // AV1 stays in Compteur; AV3 and tree counters belong to Debugging.
      const filteredCounters = response.data.filter(counter =>
        !counter.name.toLowerCase().includes('tree') && counter.name !== 'avoir3' &&
        (canReadCounters || (canReadReturnCounters && counter.name === 'avoir1'))
      );
      
      // Initialize the local state for counter values
      const newCounterValues = {};
      filteredCounters.forEach(counter => {
        newCounterValues[counter.name] = counter.value;
      });
      
      setCounters(filteredCounters);
      setCounterValues(newCounterValues);
    } catch (error) {
      message.error('Failed to fetch counters');
      console.error('Error fetching counters:', error);
    }
    setLoading(false);
  }, [API_URL, canReadCounters, canReadReturnCounters]);

  useEffect(() => { fetchCounters(); }, [fetchCounters]);

  const handleInputChange = (name, value) => {
    // Limit input to 8 characters
    if (value.toString().length <= 8) {
      setCounterValues(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSetCounter = async (name) => {
    if (!canUpdateCounter(name)) return;
    try {
      const value = counterValues[name];
      await axios.post(`${API_URL}/counter/set/${name}`, { value: parseInt(value) });
      message.success(`Counter "${name}" updated successfully`);
      
      // We don't need to call fetchCounters() here, which would cause the list to refresh
      // Instead, we've already updated our local state with the new value
    } catch (error) {
      message.error(`Failed to update counter "${name}"`);
      console.error('Error updating counter:', error);
    }
  };

  const columns = [
    {
      title: 'Counter Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      width: '40%',
    },
    {
      title: 'Current Value',
      dataIndex: 'name', // Changed to use name as the key to lookup in our local state
      key: 'value',
      sorter: (a, b) => counterValues[a.name] - counterValues[b.name],
      render: (name) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Input
            aria-label={`Valeur ${name}`}
            disabled={!canUpdateCounter(name)}
            type="number"
            value={counterValues[name]}
            onChange={(e) => handleInputChange(name, e.target.value)}
            style={{ width: '200px' }} // Made the input field longer
            onPressEnter={() => handleSetCounter(name)}
            maxLength={8} // Added maxLength property to limit input
          />
          <Button
            aria-label={`Enregistrer ${name}`}
            disabled={!canUpdateCounter(name)}
            type="primary"
            onClick={() => handleSetCounter(name)}
            style={{ marginLeft: '10px' }}
          >
            Save
          </Button>
        </div>
      ),
      width: '60%',
    },
  ];

  return (
    <Card>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2}>{canReadCounters ? 'Counter Management' : 'Avoir Counter Management'}</Title>
          <Button type="primary" onClick={fetchCounters} loading={loading}>
            Refresh Counters
          </Button>
        </div>
        
        <Table
          loading={loading}
          dataSource={counters}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          bordered
        />
      </Space>
    </Card>
  );
};

export default CounterManagement;
